import { useVirtualizer } from '@tanstack/react-virtual'
import {
  Fragment,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { HighlightBox } from '@/modules/projects/components/Workbench/highlighter'
import { filesOf } from '@/modules/projects/components/Workbench/moves'
import {
  PAGE_WIDTH,
  PageCard,
  type PageSheet,
} from '@/modules/projects/components/Workbench/PageCard'
import {
  Scrubber,
  type SegmentTint,
} from '@/modules/projects/components/Workbench/Scrubber'
import type { AccountOf } from '@/modules/projects/components/Workbench/scrubberRuler'
import {
  type BandLabel,
  type BandSource,
  bandStride,
  pagesUnderBox,
  restingPlace,
  restingScrollLeft,
  STRIP_TOP,
  type StripBand,
  scrollableWidth,
  stripLayout,
} from '@/modules/projects/components/Workbench/strip'
import { useSpringScroll } from '@/modules/projects/components/Workbench/useSpringScroll'
import { cn } from '@/utils'
import { PageJump } from './PageJump'

const BANDS_EITHER_SIDE = 3
// Set on the node under the carried pages, not in React state: see carry.ts. The mark is drawn
// inside the band, because a ring outside it would be cut off by the gap to the next band.
const DROPPING_ON =
  'data-[drop-over]:outline-2 data-[drop-over]:-outline-offset-2 data-[drop-over]:outline-blue-600 data-[drop-over]:opacity-100'

export interface StatementStripHandle {
  pagesIn: (box: HighlightBox) => number[]
  element: () => HTMLDivElement | null
  // Held while a chip drags the strip, so it waits for the release before it settles.
  grip: () => void
  letGo: () => void
}

export interface StatementStripProps {
  statements: BandSource[]
  labelFor: BandLabel
  selected: ReadonlySet<number>
  activePage: number | null
  activeStatementId: string | null
  // Nothing shows but what sits beside the scrubber. The row stays mounted at no height, so the
  // strip keeps its place for when it opens again.
  folded: boolean
  tintFor: SegmentTint
  accountOf: AccountOf
  // While pages are carried the strip creeps under them, and creeping must not re-pick the
  // statement; the snap waits until the carry ends.
  holdStill: boolean
  isLoaded: (page: number) => boolean
  srcFor: (page: number) => string | undefined
  ref?: Ref<StatementStripHandle>
  onActivate: (statementId: string) => void
  onActivatePage: (page: number, additive: boolean) => void
  onPagesWanted: (pages: number[]) => void
  onMotion: (moving: boolean) => void
}

// Which PDFs this band's pages were scanned in. The sheet numbers on the cards below only mean
// something next to it, and it is said once a band rather than once a card.
const scannedIn = (band: StripBand) => {
  const files = filesOf(band.pages.map((slot) => slot.page))
  return files.length === 1 ? files[0] : `${files.length} files`
}

// Pages already filed here have nowhere to move to, so the band does not offer a drop it would
// refuse. It is the pages in hand that decide it, not the pages the band happens to hold.
const holdsThePick = (band: StripBand, selected: ReadonlySet<number>) => {
  const here = new Set(band.pages.map((slot) => slot.page.number))
  return selected.size > 0 && [...selected].every((page) => here.has(page))
}

// The project end to end, one band per statement, the first page on the left. The open statement
// sits on a white card; the rest sit on the pane, held apart by space rather than outlines. The
// only rule drawn inside a band marks where its pages jump.
export const StatementStrip = ({
  statements,
  labelFor,
  selected,
  activePage,
  activeStatementId,
  folded,
  tintFor,
  accountOf,
  holdStill,
  isLoaded,
  srcFor,
  ref,
  onActivate,
  onActivatePage,
  onPagesWanted,
  onMotion,
}: StatementStripProps) => {
  const stripId = useId()
  const stripRef = useRef<HTMLDivElement>(null)
  const settledOn = useRef<string | null>(null)
  const onRest = useRef<((scrollLeft: number) => void) | null>(null)
  const held = useRef(holdStill)
  held.current = holdStill
  const shownStatementId = useRef(activeStatementId)
  shownStatementId.current = activeStatementId
  const [viewportWidth, setViewportWidth] = useState(0)
  const [moving, setMoving] = useState(false)

  const layout = useMemo(
    () => stripLayout(statements, labelFor, PAGE_WIDTH),
    [labelFor, statements],
  )
  const scrollWidth = scrollableWidth(layout, viewportWidth)

  const bands = useVirtualizer({
    horizontal: true,
    count: layout.bands.length,
    getScrollElement: () => stripRef.current,
    estimateSize: (index) => bandStride(layout, index),
    overscan: BANDS_EITHER_SIDE,
  })

  // The virtualizer keeps the sizes it has until the count changes, and moving pages between two
  // statements leaves the count alone. Without this it picks the bands to draw from offsets the
  // move already moved, and the strip can come up blank where the pages are.
  useEffect(() => {
    bands.measure()
  }, [bands, layout])

  // Only the pages the virtualizer is showing are worth rendering, and only once it has stopped.
  const wantPagesInWindow = useCallback(() => {
    const pages = bands
      .getVirtualItems()
      .flatMap(({ index }) => layout.bands[index]?.pages ?? [])
      .map((slot) => slot.page.number)
    if (pages.length > 0) onPagesWanted(pages)
  }, [bands, layout, onPagesWanted])

  const reportMotion = useCallback(
    (nowMoving: boolean) => {
      setMoving(nowMoving)
      onMotion(nowMoving)
    },
    [onMotion],
  )

  // The rest handler needs the spring the hook hands back, so it is reached through a ref.
  const settleHere = useCallback(
    (scrollLeft: number) => {
      wantPagesInWindow()
      if (!held.current) onRest.current?.(scrollLeft)
    },
    [wantPagesInWindow],
  )
  const { springTo, cancel, grip, letGo } = useSpringScroll({
    scrollRef: stripRef,
    onSettle: settleHere,
    onArrive: wantPagesInWindow,
    onMotion: reportMotion,
  })

  // Resting on the statement already shown tells the parent nothing new. On arrival that is the
  // first statement, and reporting it would read as the user opening it.
  const openStatement = useCallback(
    (statementId: string) => {
      if (statementId === settledOn.current) return
      settledOn.current = statementId
      if (statementId !== shownStatementId.current) onActivate(statementId)
    },
    [onActivate],
  )

  // The band abutting the left edge is the statement, told once, then eased into place.
  const goToBand = useCallback(
    (band: StripBand) => {
      openStatement(band.statementId)
      springTo(restingScrollLeft(band))
    },
    [openStatement, springTo],
  )

  // The width is read from the strip rather than from state so that a resize cannot make this
  // handler new, which would snap a parked strip back to a band start mid-drag of the divider.
  const settleOnBand = useCallback(
    (scrollLeft: number) => {
      const width = stripRef.current?.clientWidth ?? 0
      const place = restingPlace(layout, scrollLeft, width)
      if (!place) return
      openStatement(place.statementId)
      if (place.snapTo !== null) springTo(place.snapTo)
    },
    [layout, openStatement, springTo],
  )
  onRest.current = settleOnBand

  // The carry is over: settle wherever the creeping left the strip.
  useEffect(() => {
    if (holdStill || !stripRef.current) return
    settleOnBand(stripRef.current.scrollLeft)
  }, [holdStill, settleOnBand])

  // The first screenful never rested to earn its pages, so it asks for them as it appears.
  useEffect(() => {
    if (viewportWidth > 0) wantPagesInWindow()
  }, [viewportWidth, wantPagesInWindow])

  const springToStatement = useCallback(
    (statementId: string) => {
      const band = layout.bands.find((held) => held.statementId === statementId)
      if (!band) return
      settledOn.current = statementId
      springTo(restingScrollLeft(band))
    },
    [layout, springTo],
  )

  useImperativeHandle(
    ref,
    () => ({
      pagesIn: (box: HighlightBox) => pagesUnderBox(layout, box),
      element: () => stripRef.current,
      grip,
      letGo,
    }),
    [grip, layout, letGo],
  )

  // A statement picked somewhere else (a map chip, prev / next) slides the strip to it.
  useEffect(() => {
    if (!activeStatementId || activeStatementId === settledOn.current) return
    springToStatement(activeStatementId)
  }, [activeStatementId, springToStatement])

  useEffect(() => {
    const element = stripRef.current
    if (!element) return

    const measure = () => {
      setViewportWidth(element.clientWidth)
    }
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    measure()

    // A vertical wheel over a horizontal strip means along the file, which is why this listener
    // cannot be passive. Touching the strip at all drops whatever spring is running.
    const wheeled = (event: WheelEvent) => {
      cancel()
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
      event.preventDefault()
      element.scrollLeft += event.deltaY
    }
    element.addEventListener('wheel', wheeled, { passive: false })
    element.addEventListener('pointerdown', cancel)

    return () => {
      observer.disconnect()
      element.removeEventListener('wheel', wheeled)
      element.removeEventListener('pointerdown', cancel)
    }
  }, [cancel])

  // A page is drawn for real only once its image is in hand, and never while the strip is moving:
  // rendering a whole screenful of pages per frame is what a fast scrub cannot afford.
  const sheetFor = (page: number): PageSheet => {
    if (moving) return 'plain'
    return isLoaded(page) ? 'art' : 'loading'
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-auto flex-col">
      {folded ? null : (
        <div className="shrink-0 px-4">
          <Scrubber
            layout={layout}
            scrollWidth={scrollWidth}
            activeStatementId={activeStatementId}
            tintFor={tintFor}
            accountOf={accountOf}
            controls={stripId}
            scrollRef={stripRef}
            onGoToBand={goToBand}
            onScrubStart={grip}
            onScrubEnd={letGo}
            onScrubTo={(scrollLeft) => {
              cancel()
              if (stripRef.current) stripRef.current.scrollLeft = scrollLeft
            }}
          />
        </div>
      )}

      <div
        ref={stripRef}
        id={stripId}
        data-strip
        className={cn(
          'w-full overflow-y-hidden',
          folded
            ? 'h-0 shrink-0 overflow-x-hidden'
            : 'shrink-0 overflow-x-auto scrollbar-thin scrollbar-thumb-neutral-300',
        )}
      >
        <div
          className="relative"
          style={{ width: scrollWidth, height: layout.metrics.rowHeight }}
        >
          {bands.getVirtualItems().map(({ index }) => {
            const band = layout.bands[index]
            if (!band) return null
            const open = band.statementId === activeStatementId
            // Pages cannot be filed into the statement they are already held from.
            const takesADrop = !holdsThePick(band, selected)

            return (
              <div
                key={band.statementId}
                data-drop-target={takesADrop || undefined}
                data-band-drop={takesADrop ? band.statementId : undefined}
                data-band={band.statementId}
                style={{
                  left: band.left,
                  top: STRIP_TOP,
                  width: band.width,
                }}
                className={cn(
                  'group/band absolute rounded-xl border p-3',
                  open
                    ? 'border-neutral-200 bg-white'
                    : 'border-transparent hover:bg-white/60',
                  DROPPING_ON,
                )}
              >
                <p
                  data-band-statement={band.statementId}
                  title={`${band.label} · drag to scroll, ctrl-click to pick the whole statement`}
                  className={cn(
                    'h-5 truncate px-1 text-xs font-medium leading-5',
                    'cursor-grab active:cursor-grabbing',
                    takesADrop ? 'text-foreground' : 'text-blue-700',
                  )}
                >
                  {band.label}
                </p>
                <p
                  className="h-6 truncate px-1 pb-2 text-[11px] leading-4 text-ink-subtle"
                  title={scannedIn(band)}
                >
                  {scannedIn(band)}
                </p>
                <div className="relative flex gap-1">
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 hidden w-1 -translate-x-1/2 rounded-full bg-blue-600 group-data-[drop-over]/band:block"
                    style={{ left: 'var(--insert-x)' }}
                  />
                  {band.pages.map((slot) => (
                    <Fragment key={slot.page.number}>
                      {slot.afterAJump ? <PageJump /> : null}
                      <PageCard
                        page={slot.page}
                        selected={selected.has(slot.page.number)}
                        active={activePage === slot.page.number}
                        sheet={sheetFor(slot.page.number)}
                        src={srcFor(slot.page.number)}
                        onActivate={onActivatePage}
                      />
                    </Fragment>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
